<template>
  <div>
    <section id="about" class="hospitalsDetailSection">
      <div class="hospitalsDetailSection_ttl">
        <HeadingLv2 text="概要" />
      </div>
      <div class="hospitalsDetailSection_cnt">
        <section class="hospitalsDetailSection_block">
          <HeadingLv3
            class="hospitalsDetailSection_blockTtl"
            text="基本データ"
          />
          <TableSimple :rows="basicData" />
        </section>

        <section
          class="hospitalsDetailSection_block"
          v-if="isNotEmptyData(directorData)"
        >
          <HeadingLv3
            class="hospitalsDetailSection_blockTtl"
            text="院長データ"
          />
          <TableSimple :rows="directorData" />
        </section>

        <div class="hospitalsDetailSection_block hiddenPc">
          <HospitalContactBtn
            :id="detail.hospital_id"
            :paid-flg="detail.paid_flg === 1"
            @click-favorite="() => $emit('click-favorite')"
          />
        </div>
      </div>
    </section>

    <section
      v-if="detail.basicinfo1_title && detail.basicinfo1_body"
      id="basicinfo1"
      class="hospitalsDetailSection"
    >
      <div class="hospitalsDetailSection_ttl">
        <HeadingLv2 :text="detail.basicinfo1_title" />
      </div>
      <div class="hospitalsDetailSection_cnt">
        <div class="hospitalsDetailSection_block">
          <p
            class="hospitalsdetailSection_text"
            v-html="detail.basicinfo1_body"
          ></p>
        </div>
      </div>
    </section>

    <section
      class="hospitalsDetailSection"
      v-if="!!deptInfoData && deptInfoData.length > 0"
    >
      <div class="hospitalsDetailSection_ttl">
        <HeadingLv2 text="診療科情報" />
      </div>
      <div class="hospitalsDetailSection_cnt">
        <HospitalsDetailDepartmentTable :data="deptInfoData" />
      </div>
    </section>

    <section id="feature" class="hospitalsDetailSection">
      <div class="hospitalsDetailSection_ttl">
        <HeadingLv2 text="特徴" />
      </div>
      <div class="hospitalsDetailSection_cnt">
        <div class="hospitalsDetailSection_block">
          <HospitalTag :tags="features" lg />
        </div>
      </div>
    </section>

    <section
      v-if="detail.basicinfo2_title && detail.basicinfo2_body"
      id="basicinfo2"
      class="hospitalsDetailSection"
    >
      <div class="hospitalsDetailSection_ttl">
        <HeadingLv2 :text="detail.basicinfo2_title" />
      </div>
      <div class="hospitalsDetailSection_cnt">
        <div class="hospitalsDetailSection_block">
          <p
            class="hospitalsdetailSection_text"
            v-html="detail.basicinfo2_body"
          ></p>
        </div>
      </div>
    </section>

    <section id="access" class="hospitalsDetailSection">
      <div class="hospitalsDetailSection_ttl">
        <HeadingLv2 text="アクセス" />
      </div>
      <div class="hospitalsDetailSection_cnt">
        <TableSimple :rows="accessData" />

        <div class="hospitalsDetailSection_access">
          <div class="hospitalsDetailSection_map">
            <iframe
              :src="
                `https://www.google.com/maps/embed/v1/place?key=${GMAP_EMBED_API_KEY}&q=${mapQuery}`
              "
              width="100%"
              height="100%"
              frameborder="0"
              style="border:0;"
              allowfullscreen=""
              aria-hidden="false"
              tabindex="0"
              loading="lazy"
            ></iframe>
          </div>
          <a
            :href="
              `https://www.google.com/maps/search/?api=1&query=${mapQuery}`
            "
            target="_blank"
            >Googleマップで開く</a
          >
        </div>
      </div>
    </section>

    <section
      v-if="detail.basicinfo3_title && detail.basicinfo3_body"
      id="basicinfo3"
      class="hospitalsDetailSection"
    >
      <div class="hospitalsDetailSection_ttl">
        <HeadingLv2 :text="detail.basicinfo3_title" />
      </div>
      <div class="hospitalsDetailSection_cnt">
        <div class="hospitalsDetailSection_block">
          <p
            class="hospitalsdetailSection_text"
            v-html="detail.basicinfo3_body"
          ></p>
        </div>
      </div>
    </section>

    <div class="hospitalsDetailSection hiddenPc">
      <div class="hospitalsDetailSection_cnt">
        <div class="hospitalsDetailSection_block">
          <HospitalContactBtn
            :id="detail.hospital_id"
            :paid-flg="detail.paid_flg === 1"
            @click-favorite="() => $emit('click-favorite')"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue'
import TableSimple from '@/components/elements/TableSimple.vue'
import HeadingLv2 from '@/components/elements/HeadingLv2.vue'
import HeadingLv3 from '@/components/elements/HeadingLv3.vue'
import HospitalTag from '@/components/elements/HospitalTag.vue'
import HospitalContactBtn from '@/components/block/HospitalContactBtn.vue'
import HospitalsDetailDepartmentTable from '@/components/block/HospitalsDetailDepartmentTable.vue'
import { numberFormatAddComma } from '@/utils/commonMethods'
import { GMAP_EMBED_API_KEY } from '@/utils/commonDataset'
import { getDepartmentsData } from "@/utils/hospitals/getDepartmentsData";

export default defineComponent({
  components: {
    TableSimple,
    HeadingLv2,
    HeadingLv3,
    HospitalTag,
    HospitalContactBtn,
    HospitalsDetailDepartmentTable
  },

  props: {
    detail: {
      type: Object,
      default: () => ({})
    },
    state: {
      type: Object,
      default: () => ({})
    },
    features: {
      type: Array,
      default: () => []
    }
  },

  setup(props) {
    const departmentsData = computed(() => getDepartmentsData(props.detail))

    const basicData = computed(() => {
      return [
        {
          th: '病院名',
          td: props.detail.hospital_name,
          kana: props.detail.hospital_name_kana
            ? `(${props.detail.hospital_name_kana})`
            : ''
        },
        {
          th: '所在地',
          td: props.detail.prefectures + props.detail.hospital_address
        },
        { th: '病院種別', td: props.detail.hospital_class },
        {
          th: '救急指定',
          td: props.detail.designated_hospital
            ? props.detail.designated_hospital.join('、')
            : ''
        },
        {
          th: '病床数',
          td: props.detail.bed_num ? props.detail.bed_num + '床' : ''
        },
        { th: '病床数内訳', td: props.detail.bed_detail || '' },
        { th: '病院URL', td: props.detail.hospital_url || '', link: true },
        {
          th: '募集定員',
          td: props.detail.positions_num
            ? numberFormatAddComma(props.detail.positions_num) + '名'
            : ''
        },
        {
          th: '診療科目',
          td: departmentsData.value
        }
      ]
    })

    const directorData = computed(() => {
      return [
        { th: '院長名', td: props.detail.director_name },
        { th: '出身大学', td: props.detail.director_graduating_school || '' },
        {
          th: '専門科目',
          td: props.detail.director_specialty
            ? props.detail.director_specialty.join('、')
            : ''
        }
      ]
    })
    const doctorData = computed(() => {
      return [
        {
          th: '医師数',
          td: props.detail.doctor_num
            ? numberFormatAddComma(props.detail.doctor_num) + '名'
            : ''
        }
      ]
    })
    const patientData = computed(() => {
      return [
        {
          th: '外来患者数（1日平均）',
          td: props.detail.outpatient_num_avg
            ? numberFormatAddComma(props.detail.outpatient_num_avg) + '名'
            : ''
        },
        {
          th: '入院患者数（1日平均）',
          td: props.detail.inpatient_num_avg
            ? numberFormatAddComma(props.detail.inpatient_num_avg) + '名'
            : ''
        },
        {
          th: '救急外来患者 年間件数',
          td: props.detail.emergency_num_avg
            ? numberFormatAddComma(props.detail.emergency_num_avg) + '件'
            : '',
          thChild: 'うち時間外件数',
          tdChild: props.detail.out_of_hours
            ? numberFormatAddComma(props.detail.out_of_hours) + '件'
            : '',
          smRowLayout: true
        },
        {
          th: '救急車搬送 年間件数',
          td: props.detail.ambulance_num
            ? numberFormatAddComma(props.detail.ambulance_num) + '件'
            : '',
          thChild: 'うち時間外件数',
          tdChild: props.detail.ambulance_out_of_hours
            ? numberFormatAddComma(props.detail.ambulance_out_of_hours) + '件'
            : '',
          smRowLayout: true
        }
      ]
    })
    const accessData = computed(() => {
      return [
        {
          th: '所在地',
          td: `〒${props.detail.post_code}<br>${props.detail.prefectures}${props.detail.hospital_address}`
        }
      ]
    })
    const mapQuery = computed(() => {
      return props.detail.latitude && props.detail.longitude
        ? `${props.detail.latitude},${props.detail.longitude}`
        : `${props.detail.prefectures}${props.detail.hospital_address}`
    })
    const isNotEmptyData = (data: any) => {
      let res = false
      data.forEach((el: any) => {
        if (el.td) res = true
      })
      return res
    }

    const deptInfoData = computed(() => {
      return props.detail.dept_info ? JSON.parse(props.detail.dept_info) : []
    })

    return {
      basicData,
      directorData,
      doctorData,
      patientData,
      accessData,
      mapQuery,
      isNotEmptyData,
      numberFormatAddComma,
      GMAP_EMBED_API_KEY,
      deptInfoData
    }
  }
})
</script>

<style lang="scss" scoped>
@use 'src/styles/abstracts/mixins';
@use 'src/styles/abstracts/variables' as var;

.hospitalsDetailSection_access {
  margin-top: 10px;
  text-align: right;

  @include mixins.media() {
    margin-top: 20px;
  }

  a {
    color: var.$color-main;
    display: inline-block;
    margin-top: 8px;

    @include mixins.media() {
      display: none;
    }
  }
}
.hospitalsDetailSection_map {
  position: relative;
  width: 100%;
  height: 0;
  padding-top: 75%;

  @include mixins.media() {
    padding-top: 50%;
  }

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
}
</style>
